// ─────────────────────────────────────────────────────────────────────────────
// Buckly's Today API (B-4.1b): GET /api/buckly/today, read the house way (axios, apiBase, authToken —
// lib/mackCalculatorRead.ts). Her page reads it once for its header and its Today tab, and her
// Overview card reads it for "State:". The server answers each panel with its data, or "no_manual"
// while Matt's manual for it is not current in her store; a panel with no data keeps the design's
// drawn placeholder. A read that fails answers null, and nothing is drawn from it.
// ─────────────────────────────────────────────────────────────────────────────

import axios from 'axios';
import { useEffect, useState } from 'react';
import { apiBase, authToken } from '../../api/apiBase';

export type NoManual = 'no_manual';
export type Mark = 'UNKNOWN' | 'ABSENT' | 'UNAVAILABLE';

export interface WriterPanel<R> {
  state: 'ok' | 'unavailable';
  reading: R | null;
  reading_at: string | null;
  written?: boolean;
}

export interface YourStateReading {
  date: string;
  readiness_pct: number | Mark;
  score_version: string;
  state_label: string;
  watch_badge: string;
  why_line: string[];
  actions: string[];
  read_text: string;
  composed_from: { n_trades: number; n_journals: number; framework_refs: string[] };
  source_refs: string[];
  gaps: string[];
}

export interface YouVsRow {
  dimension: 'size_discipline' | 'trades_per_session' | 'patience';
  now_value: number | Mark;
  now_n: number | Mark;
  at_best_value: number | string;
  score_version: string;
}

export interface YouVsReading {
  date: string;
  week: string;
  rows: YouVsRow[];
  gaps: string[];
}

export interface RecentRow {
  date: string;
  day_label: string;
  trades: number;
  open: number;
  r_day: number | 'open' | 'UNKNOWN';
  note_items: string[];
  journal_present: boolean;
}

export interface RecentSessions {
  state: 'ok' | 'unavailable';
  rows: RecentRow[];
  today: { status_note: string; live_status: string };
  window_sessions: number;
}

export interface FleetHelper {
  name: string;
  maps_to: string;
  run_state: 'live' | 'failed' | 'not run yet' | 'not built yet';
  status_line: string;
  last_run: string | null;
}

export interface Fleet {
  helpers: FleetHelper[];
  live_count: number;
}

export interface Header {
  status_line: string;
}

export interface BucklyToday {
  your_state: WriterPanel<YourStateReading> | NoManual;
  recent_sessions: RecentSessions | NoManual;
  you_vs_best: WriterPanel<YouVsReading> | NoManual;
  fleet: Fleet | NoManual;
  header: Header | NoManual;
}

/** One panel's data, or null while its manual is not current (or nothing was read). */
export function panel<K extends keyof BucklyToday>(today: BucklyToday | null | undefined, key: K):
  Exclude<BucklyToday[K], NoManual> | null {
  const value = today ? today[key] : null;
  if (!value || value === 'no_manual' || typeof value !== 'object') return null;
  return value as Exclude<BucklyToday[K], NoManual>;
}

export async function readToday(token?: string | null): Promise<BucklyToday | null> {
  try {
    const response = await axios.get(`${apiBase()}/buckly/today`, {
      headers: { Authorization: `Bearer ${token || authToken()}`, 'Cache-Control': 'no-cache' },
    });
    return response.data && typeof response.data === 'object' ? (response.data as BucklyToday) : null;
  } catch {
    return null;
  }
}

/** Her Today, read when the caller mounts and again whenever `refresh` changes. */
export function useBucklyToday(token?: string | null, refresh?: unknown): BucklyToday | null {
  const [today, setToday] = useState<BucklyToday | null>(null);
  useEffect(() => {
    let live = true;
    readToday(token).then((got) => { if (live) setToday(got); });
    return () => { live = false; };
  }, [token, refresh]);
  return today;
}
